import { EFinancingProgram, EWorksheetDecision } from '@if/core/types'
import { Grid, Stack } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import PendingMessage from '@src/components/PendingMessage'
import { MechanicalWarrantyComputeRequestDto } from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyRequestDto'
import {
    EMechanicalWarrantyOptions,
    EMechanicalWarrantySurcharges,
    MechanicalWarrantyDecisionDTO,
} from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/mechanicalWarrantyWorksheetSchema'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { useAppDispatch, useAppSelector } from '@src/data/store'
import { appSelectors } from '@src/data/store/AppStore'
import { availableCreditSelectors } from '@src/data/store/AvailableCredit/available-credit-store'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { documentSelectors } from '@src/data/store/Document'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { fullCreditApplicationEffects } from '@src/data/store/FullCreditApplication'
import {
    mechanicalWarrantyWorksheetActions,
    mechanicalWarrantyWorksheetSelectors,
} from '@src/data/store/MechanicalWarrantyWorksheet'
import mechanicalWarrantyWorksheetEffects from '@src/data/store/MechanicalWarrantyWorksheet/mechanical-warranty-worksheet-effects'
import { messageEffects, messageSelectors } from '@src/data/store/Message'
import { userSelectors } from '@src/data/store/UserStore'
import {
    BaseWorksheetFundingThresholdExtend,
    CreditApplicationMessage,
    NormsMessage,
    WorksheetDecision,
} from '@src/data/types'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MechanicalWarrantySummaryBox from '../components/LoanSummary/MechanicalWarrantySummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import BorrowerSection from '../components/Transaction/BorrowerSection'
import FinancingSection from '../components/Transaction/FinancingSection'
import MechanicalWarrantySection from '../components/Transaction/MechanicalWarrantySection'
import SectionContainer from '../components/Transaction/SectionContainer'
import { useDialogDecisionDefaultValue } from '../viewWorksheet-hooks'
import { getEmptyMechanicalWarrantyComputeResponse } from '../viewWorksheet-selectors'

const MechanicalWarrantyWorksheetViewPage = () => {
  const { t } = useTranslation()
  const dispatchEffect = useSideEffect()
  const dispatch = useAppDispatch()
  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')
  const isLoading = useAppSelector(creditSelectors.isLoadingCreditApplication)
  const creditApplication = useAppSelector(creditSelectors.getCurrent)
  const mechanicalWorksheet = useAppSelector(mechanicalWarrantyWorksheetSelectors.getCurrent)
  const finalDecisionIsPending = useAppSelector(creditSelectors.isFinalDecisionPending)
  const error = useAppSelector(appSelectors.getBusinessError)
  const availableCredit = useAppSelector(availableCreditSelectors.getAvailableCredit)
  const creditInProgress = useAppSelector(availableCreditSelectors.getCreditInProgress)
  const commonNormsMessage: NormsMessage[] = useAppSelector(creditSelectors.getCurrentCreditApplicationSpecificNorms)
  const user = useAppSelector(userSelectors.getUser)
  const privateMessages = useAppSelector(messageSelectors.getPrivateMessages)
  const computedInfo = useAppSelector(mechanicalWarrantyWorksheetSelectors.getComputedInfo)
  const worksheetNormWarningMessages = useAppSelector(
    mechanicalWarrantyWorksheetSelectors.getCurrentWorksheetNormWarningMessages,
  )
  const areAllTasksRequiredForCVTCompleted = useAppSelector(creditSelectors.areAllTasksRequiredForCVTCompleted)
  const areAllDocumentsRequiredForCVTApproved = useAppSelector(documentSelectors.areAllDocumentsRequiredForCVT)
  const confirmAllIncomesStatus = useAppSelector(creditSelectors.getConfirmAllIncomesStatus)
  const totalObligation = mechanicalWorksheet?.amountRequested ?? 0
  const activePaymentPlan = useAppSelector(creditSelectors.getMerchantPaymentPlan)

  const mechanicalWarrantyProviders = useAppSelector(mechanicalWarrantyWorksheetSelectors.getProviders)
  const mechanicalProducts = useAppSelector(mechanicalWarrantyWorksheetSelectors.getProductsByProvider)

  useEffect(() => {
    if (creditApplication?.merchantId) {
      dispatchEffect(
        mechanicalWarrantyWorksheetEffects.getMechanicalWarrantyProviders(creditApplication.merchantId),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication?.merchantId, dispatchEffect])

  useEffect(() => {
    if (mechanicalWorksheet?.providerId && creditApplication?.merchantId) {
      dispatchEffect(
        mechanicalWarrantyWorksheetEffects.getMechanicalWarrantyProviderProducts(
          creditApplication.merchantId,
          mechanicalWorksheet.providerId,
        ),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication?.merchantId, dispatchEffect, mechanicalWorksheet?.providerId])

  const selectedProvider = mechanicalWarrantyProviders.find(
      provider => provider.id === mechanicalWorksheet?.providerId
    )

  const selectedMechanicalProduct = mechanicalProducts.find(
      product => product.id === mechanicalWorksheet?.mechanicalWarrantyProductId
    )

  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending
  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.MechanicalWarranty}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    mechanicalWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const handleDeletingPrivateMessage = async (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      await dispatchEffect(messageEffects.deleteMessage(message, creditApplication.financingProgramId))
    }
  }


  useEffect(() => {
    if (creditApplication && mechanicalWorksheet && Object.keys(computedInfo).length === 0) {
      const computeDto: MechanicalWarrantyComputeRequestDto = {
        paymentFrequency: mechanicalWorksheet.paymentFrequency,
        term: mechanicalWorksheet.term,
        firstPaymentDate: mechanicalWorksheet.firstPaymentOn,
        deliveryOn: mechanicalWorksheet.deliveryOn,
        options: mechanicalWorksheet.additions.options as unknown as EMechanicalWarrantyOptions[],
        surcharges: mechanicalWorksheet.additions.surcharges as unknown as EMechanicalWarrantySurcharges[],
        creditApplicationId: creditApplication.id,
        providerId: mechanicalWorksheet.providerId,
        mechanicalWarrantyProductId: mechanicalWorksheet.mechanicalWarrantyProductId,
      }

      if (computeDto) {
        dispatchEffect(mechanicalWarrantyWorksheetEffects.computeMechanicalWarrantyLoan(computeDto)).catch(
          reportErrorToConsole,
        )
      } else {
        const emptyComputedDto = getEmptyMechanicalWarrantyComputeResponse()

        dispatch(mechanicalWarrantyWorksheetActions.setLoanComputed(emptyComputedDto))
      }
    }
  }, [mechanicalWorksheet, computedInfo, creditApplication, dispatchEffect, dispatch])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (mechanicalWorksheet) {
        const updatedFinalDecision: MechanicalWarrantyDecisionDTO = {
          creditApplicationId: mechanicalWorksheet.creditApplicationId,
          decision: data.decision,
          otherReason: data.otherReason,
          versionTag: mechanicalWorksheet.versionTag,
        } as MechanicalWarrantyDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        await dispatchEffect(
          mechanicalWarrantyWorksheetEffects.updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: creditApplication?.id,
          }),
        ).then(async () => {
          await dispatchEffect(
            fullCreditApplicationEffects.getById(creditApplication!.id, EFinancingProgram.MechanicalWarranty),
          )
          closeDialogs()
        })
      }
    },
    [mechanicalWorksheet, creditApplication, closeDialogs, dispatchEffect],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && mechanicalWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField value="GM" label={t('worksheetCommon.program')} disabled />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}
            <PrivateMessage
              messages={privateMessages}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              currentUser={user}
              creditAppCreationDate={creditApplication.createdOn}
            />
            <Grid container textAlign="center" justifyContent="space-around" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <MechanicalWarrantySummaryBox
                  worksheet={mechanicalWorksheet}
                  computedInfo={computedInfo}
                  interestsRate={creditApplication.finalCreditDecision.interestRate}
                />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={mechanicalWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>
          {selectedProvider && selectedMechanicalProduct && (
        <SectionContainer>
          <FinancingSection
            worksheet={mechanicalWorksheet}
            creditApplication={creditApplication}
            totalObligation={totalObligation}
            merchantPaymentPlan={activePaymentPlan ?? null}
          />
          <BorrowerSection worksheet={mechanicalWorksheet} />
          <MechanicalWarrantySection
            mechanicalWarrantyProvider={selectedProvider}
            mechanicalProduct={selectedMechanicalProduct}
          />
        </SectionContainer>
          )}
          </Stack>
          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={mechanicalWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default MechanicalWarrantyWorksheetViewPage
